import { gameService, type Game } from '../../../services/gameService';
import {
  gameDateKey,
  listUpcomingGamesSorted,
  loadFieldingEntriesForCoachEdit,
  mergePitcherInnings,
  mergePositionAllInnings,
  persistGameFielding,
  persistManualBattingOrder,
  tryResolveGameFromMessage,
} from '../../../services/gameLineupCoachService';
import { getGameFieldingInningsCount } from '../../../utils/gameFieldingInnings';
import { getEventLabel } from '../../../data/sportRulesets';
import type { AgentRunContext, AgentRunResult, PendingGameLineupPick } from './types';
import {
  extractBaseballSinglePositionAssignment,
  extractSingleInningHint,
  findPlayersInSegment,
  isLikelyBattingOrderOnlyMessage,
  looksLikePitchingRotationMessage,
  parseBattingOrderList,
  parseSequentialPitchingRotation,
  resolveBattingSlots,
} from './gameLineupParsing';
import {
  fetchRosterForInjury,
  findPlayersForInjury,
  playerRefToIdentifier,
} from './playerLookup';
import { SPORT_POSITIONS } from '../../../data/sportsPositions';
import { runHelpAgent } from './helpAgent';

export function buildGamePickPromptLine(g: Game, index: number): { id: string; label: string; date: string; opponent: string } {
  const date = gameDateKey(g.date);
  return {
    id: g.id,
    date,
    opponent: g.opponent_name,
    label: `${index}. **vs ${g.opponent_name}** · **${date}** · ${g.home_away}`,
  };
}

/**
 * Baseball / softball: resolve game when needed; set pending pick or execute instruction.
 */
export async function handleBaseballSoftballDepthRequest(
  text: string,
  ctx: AgentRunContext,
  options?: { chosenGame?: Game | null }
): Promise<AgentRunResult> {
  const team = ctx.team;
  const eventLabel = getEventLabel(team.sport);
  ctx.setIsLoading(true);
  try {
    const allGames = await gameService.getGames(team.id);
    let chosen: Game | null = options?.chosenGame ?? null;

    if (!chosen) chosen = tryResolveGameFromMessage(text, allGames);

    if (!chosen) {
      const upcoming = listUpcomingGamesSorted(allGames);
      if (upcoming.length === 0) {
        ctx.addMessage(
          'assistant',
          `There aren't any upcoming ${eventLabel.plural.toLowerCase()} on the schedule yet. **Create a ${eventLabel.singular.toLowerCase()}** first, then I can save pitching, fielding, and batting order for that ${eventLabel.singular.toLowerCase()}.`,
          { label: eventLabel.plural, href: '/games' }
        );
        return { kind: 'handled' };
      }
      const limit = Math.min(upcoming.length, 6);
      const candidates = upcoming.slice(0, limit).map((g, i) => buildGamePickPromptLine(g, i + 1));
      ctx.setPendingGameLineupPick({
        phase: 'needGamePick',
        instruction: text.trim(),
        candidates,
      });
      const listed = candidates.map((c) => c.label).join('\n');
      ctx.addMessage(
        'assistant',
        `Which game should this apply to?\n\n${listed}\n\nReply with a **number**, the **opponent name**, or repeat your request **including the opponent or date** (e.g. **vs Wildcats on ${candidates[0]?.date ?? '…'}**).`,
        { label: 'Open schedule', href: '/games' }
      );
      return { kind: 'needs_input' };
    }

    return await applyBaseballInstructionToGame(chosen, text, ctx);
  } catch (err) {
    console.error(err);
    ctx.addMessage('assistant', "Something went wrong reading or saving the game. Try again from the **game** page if it keeps happening.");
    return { kind: 'error', error: String(err) };
  } finally {
    ctx.setIsLoading(false);
  }
}

export async function continuePendingGameLineupPick(
  reply: string,
  pending: PendingGameLineupPick,
  ctx: AgentRunContext
): Promise<void> {
  if (pending.phase !== 'needGamePick') return;
  const trimmed = reply.trim();
  const n = parseInt(trimmed, 10);
  let gameId: string | null = null;
  if (/^\d+$/.test(trimmed) && n >= 1 && n <= pending.candidates.length) {
    gameId = pending.candidates[n - 1].id;
  } else {
    const lower = trimmed.toLowerCase();
    const hit = pending.candidates.find(
      (c) =>
        c.opponent.toLowerCase() === lower ||
        c.opponent.toLowerCase().includes(lower) ||
        (lower.length >= 3 && lower.includes(c.opponent.toLowerCase().slice(0, Math.min(5, c.opponent.length))))
    );
    gameId = hit?.id ?? null;
  }

  if (!gameId) {
    const resolved = tryResolveGameFromMessage(trimmed, await gameService.getGames(ctx.team.id));
    if (resolved) gameId = resolved.id;
  }

  if (!gameId) {
    ctx.addMessage(
      'assistant',
      "I still need to know which game. Reply with **1**, **2**, … from the list, or **vs [opponent]** / a **YYYY-MM-DD** date."
    );
    return;
  }

  ctx.setPendingGameLineupPick(null);
  const game = await gameService.getGame(gameId);
  if (!game) {
    const eventLabel = getEventLabel(ctx.team.sport);
    ctx.addMessage('assistant', `That ${eventLabel.singular.toLowerCase()} record wasn't found. Open **${eventLabel.plural}** and try again.`);
    return;
  }
  await applyBaseballInstructionToGame(game, pending.instruction, ctx);
}

async function applyBaseballInstructionToGame(game: Game, text: string, ctx: AgentRunContext): Promise<AgentRunResult> {
  const team = ctx.team;
  const roster = await fetchRosterForInjury(team.id);
  const maxInn = getGameFieldingInningsCount(team);
  const gameHref = `/games/${game.id}`;

  const pitchingFirst =
    looksLikePitchingRotationMessage(text) || (splitRoughClauses(text).length > 1 && /\b(?:pitch|throw|mound)\b/i.test(text));

  if (pitchingFirst) {
    const blocks = parseSequentialPitchingRotation(text, roster, maxInn);
    if (!blocks) {
      ctx.addMessage(
        'assistant',
        `I pulled up **vs ${game.opponent_name}** on **${gameDateKey(game.date)}**, but I couldn't match every pitcher cleanly to exactly one roster name. Separate each pitcher with a comma or **then** (e.g. **Maya 2 innings, then Jordan 3 innings**) and I'll save it.`,
        { label: 'Game detail', href: gameHref }
      );
      return { kind: 'needs_input' };
    }

    ctx.setIsLoading(true);
    try {
      let existing = await loadFieldingEntriesForCoachEdit(game.id, team.id);
      const mergeBlocks = blocks.map((b) => ({
        playerId: b.playerId,
        fromInning: b.fromInning,
        toInning: b.toInning,
      }));
      existing = mergePitcherInnings(existing, mergeBlocks, 'P');
      await persistGameFielding(game.id, existing, team);
      await ctx.refreshTeamContextForAgent();
      const summarize = blocks
        .map((b) => `**${b.playerLabel}** innings **${b.fromInning}${b.fromInning === b.toInning ? '' : `–${b.toInning}`}**`)
        .join('; ');
      ctx.addMessage(
        'assistant',
        `Saved pitching for **vs ${game.opponent_name}** (${gameDateKey(game.date)}): ${summarize}. Open the game to review the full fielding grid.`,
        { label: 'Open game', href: gameHref }
      );
      return { kind: 'handled' };
    } catch (e) {
      console.error(e);
      ctx.addMessage('assistant', "Couldn't save pitching to that game. Check the **Fielding** tab on the game page.");
      return { kind: 'error', error: String(e) };
    } finally {
      ctx.setIsLoading(false);
    }
  }

  const battingOrderSlots = isLikelyBattingOrderOnlyMessage(text) ? parseBattingOrderList(text) : null;
  if (isLikelyBattingOrderOnlyMessage(text) && !battingOrderSlots) {
    // Text mentions batting order but has no parseable numbered list.
    // If it looks like the coach attempted a list (e.g. "9. Name"), show the format hint.
    // Otherwise it's a natural language request — fall through to the LLM.
    if (/\d{1,2}\s*[.)]\s*[A-Za-z]/i.test(text)) {
      ctx.addMessage(
        'assistant',
        `For **vs ${game.opponent_name}**, list batting order like: **1. Alex 2. Sam 3. Jordan** — or open the game's **Lineup** tab.`,
        { label: 'Game detail', href: gameHref }
      );
      return { kind: 'needs_input' };
    }
  }
  if (battingOrderSlots && battingOrderSlots.length >= 3) {
    const resolved = resolveBattingSlots(battingOrderSlots, roster);
    if ('error' in resolved) {
      ctx.addMessage('assistant', `${resolved.error} Which game lineup did you mean is already selected — tighten the spelling or use **# jersey**.`, {
        label: 'Game detail',
        href: gameHref,
      });
      return { kind: 'needs_input' };
    }
    ctx.setIsLoading(true);
    try {
      await persistManualBattingOrder(
        game.id,
        resolved.ok.map((e) => ({ batting_order: e.batting_order, player_id: e.player_id })),
        game.lineup_source
      );
      await ctx.refreshTeamContextForAgent();
      const names = resolved.ok.map((e) => `${e.batting_order}. **${e.label}**`).join(', ');
      ctx.addMessage(
        'assistant',
        `Saved batting order for **vs ${game.opponent_name}** (${gameDateKey(game.date)}): ${names}.`,
        { label: 'Open game', href: gameHref }
      );
      return { kind: 'handled' };
    } catch (e) {
      console.error(e);
      ctx.addMessage('assistant', "Couldn't save batting order — use **Set lineup** on the game page.");
      return { kind: 'error', error: String(e) };
    } finally {
      ctx.setIsLoading(false);
    }
  }

  const assign = extractBaseballSinglePositionAssignment(text);
  if (assign) {
    const sportKey = team.sport === 'Softball' ? 'Softball' : 'Baseball';
    const allowed = new Set(SPORT_POSITIONS[sportKey].Defense.map((p) => p.key));
    if (!allowed.has(assign.positionKey)) {
      ctx.addMessage(
        'assistant',
        `**${assign.positionKey}** is not a defensive position for **${sportKey}**. Use **P**, **C**, **1B**, **2B**, **3B**, **SS**, **LF**, **CF**, **RF**${sportKey === 'Baseball' ? ', **DH**' : ''}.`
      );
      return { kind: 'needs_input' };
    }
    const identifier = playerRefToIdentifier(assign.playerRef);
    const matches = findPlayersForInjury(roster, identifier);
    if (matches.length === 0) {
      ctx.addMessage('assistant', "I don't see that player on the roster. Use jersey number or first and last name.");
      return { kind: 'needs_input' };
    }
    if (matches.length > 1) {
      const list = matches.map((p) => `**${p.first_name} ${p.last_name}**${p.number != null ? ` #${p.number}` : ''}`).join(', ');
      ctx.addMessage('assistant', `I found more than one player: ${list}. Which jersey number should I use?`);
      return { kind: 'needs_input' };
    }
    const player = matches[0];
    const inn = extractSingleInningHint(text);
    ctx.setIsLoading(true);
    try {
      let existing = await loadFieldingEntriesForCoachEdit(game.id, team.id);
      if (assign.positionKey === 'P' && inn == null) {
        existing = mergePitcherInnings(existing, [{ playerId: player.id, fromInning: 1, toInning: maxInn }], 'P');
      } else if (assign.positionKey === 'P' && inn != null) {
        existing = mergePitcherInnings(existing, [{ playerId: player.id, fromInning: inn, toInning: inn }], 'P');
      } else if (inn != null) {
        const next = existing.filter((e) => !(e.position === assign.positionKey && e.inning === inn));
        next.push({ inning: inn, position: assign.positionKey, player_id: player.id });
        existing = next;
      } else {
        existing = mergePositionAllInnings(existing, assign.positionKey, player.id, maxInn);
      }

      await persistGameFielding(game.id, existing, team);
      await ctx.refreshTeamContextForAgent();
      const name = `${player.first_name} ${player.last_name}${player.number != null ? ` #${player.number}` : ''}`;
      const inningNote = inn != null ? ` for **inning ${inn}**` : '';
      ctx.addMessage(
        'assistant',
        `Saved **${name}** at **${assign.positionKey}**${inningNote} for **vs ${game.opponent_name}**.`,
        { label: 'Open game', href: gameHref }
      );
      return { kind: 'handled' };
    } catch (e) {
      console.error(e);
      ctx.addMessage('assistant', "Couldn't save fielding positions to that game.");
      return { kind: 'error', error: String(e) };
    } finally {
      ctx.setIsLoading(false);
    }
  }

  // Before falling back to the LLM, check whether the coach is confirming a
  // previously suggested batting order (e.g. said "yes" or "the lineup was
  // never set"). Scan recent assistant messages for the newest parseable order.
  if (await saveRecentBattingOrderFromHistory(game, roster, ctx)) {
    return { kind: 'handled' };
  }

  // Natural language request that didn't match any structured parser — let the LLM
  // handle it with full roster/stats context. Pass the resolved game as system
  // context so the model knows which game is selected without us polluting
  // the user-message turn with stale or synthetic text.
  return runHelpAgent(
    text,
    ctx,
    `Game selected: vs ${game.opponent_name} on ${gameDateKey(game.date)}. If the coach is confirming or applying a previously suggested batting order, list the order and remind them to copy-paste it back in "1. Name 2. Name …" format so I can save it.`
  );
}

/**
 * Scan recent assistant messages (newest first) for a parseable batting order
 * that was suggested by the AI. If one is found with at least 3 resolvable
 * players, save it and return true; otherwise return false.
 *
 * Used when the coach confirms a suggestion with "yes", "save it", etc., or
 * reports "the lineup was never set" after the LLM claimed it did.
 */
async function saveRecentBattingOrderFromHistory(
  game: Game,
  roster: RosterPlayerForInjury[],
  ctx: AgentRunContext
): Promise<boolean> {
  const gameHref = `/games/${game.id}`;
  const messages = [...ctx.recentMessages].reverse();
  for (const msg of messages) {
    if (msg.role !== 'assistant') continue;
    // Strip markdown bold/italic so "**Aiden Mann**" parses as "Aiden Mann".
    const plainContent = msg.content.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/_([^_]+)_/g, '$1');
    const slots = parseBattingOrderList(plainContent);
    if (!slots || slots.length < 3) continue;
    // Best-effort: resolve as many players as possible; skip unresolvable ones
    // rather than abandoning the whole order on a single mismatch.
    const okEntries: Array<{ batting_order: number; player_id: string; label: string }> = [];
    for (const s of slots) {
      const matches = findPlayersInSegment(roster, s.nameTokens);
      if (matches.length === 1) {
        const p = matches[0];
        okEntries.push({
          batting_order: s.battingOrder,
          player_id: p.id,
          label: `${p.first_name} ${p.last_name}`.trim(),
        });
      }
    }
    if (okEntries.length < 3) continue;
    ctx.setIsLoading(true);
    try {
      await persistManualBattingOrder(
        game.id,
        okEntries.map((e) => ({ batting_order: e.batting_order, player_id: e.player_id })),
        game.lineup_source
      );
      await ctx.refreshTeamContextForAgent();
      const names = okEntries.map((e) => `${e.batting_order}. **${e.label}**`).join(', ');
      ctx.addMessage(
        'assistant',
        `Saved batting order for **vs ${game.opponent_name}** (${gameDateKey(game.date)}): ${names}.`,
        { label: 'Open game', href: gameHref }
      );
    } catch (e) {
      console.error(e);
      ctx.addMessage('assistant', "Couldn't save batting order — use **Set lineup** on the game page.");
    } finally {
      ctx.setIsLoading(false);
    }
    return true;
  }
  return false;
}

function splitRoughClauses(text: string): string[] {
  return text
    .split(/\s*(?:,|;|(?:\s+then\s+)|(?:\s+followed\s+by\s+))\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Generic guidance — per-game roster positions are modeled for baseball/softball only today. */
export function handleDepthChartCoachMessageForTeamSport(text: string, ctx: AgentRunContext): AgentRunResult {
  const sport = ctx.team.sport;
  ctx.addMessage(
    'assistant',
    sport === 'Hockey'
      ? '**Line shifts** aren’t saved on the game sheet in Hustli yet — use **Depth Chart** to stack lines. If you meant **Baseball / Softball** pitching or lineup on a calendar game, switch to that team.'
      : sport === 'Volleyball'
        ? 'Practice **rotations / lineups** in **Depth Chart** for now; per-calendar-game lineup edits in chat aren’t wired for volleyball yet.'
        : `Use **Depth Chart** to organize **${sport}** starters and rotations. For **baseball / softball** teams you can tell me pitching, batting order, and defensive spots **for a specific game** once you confirm which game.`,
    { label: 'Open Depth Chart', href: '/depth-chart' }
  );
  return { kind: 'handled' };
}
